<script setup>
import { computed, ref, onErrorCaptured } from 'vue'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'

const props = defineProps({
  // Accepts either a parsed object/array or a raw JSON string
  data: {
    type: [Object, Array, String],
    default: null,
  },
})

const renderError = ref(false)

// Try to parse if a string is passed in; return null on failure
const parsed = computed(() => {
  if (props.data === null || props.data === undefined) return null
  if (typeof props.data === 'string') {
    if (props.data.trim() === '') return null
    try {
      return JSON.parse(props.data)
    } catch {
      return null // fallback to raw display below
    }
  }
  return props.data
})

const rawFallback = computed(() => {
  if (!props.data) return ''
  if (typeof props.data === 'string') return props.data
  try {
    return JSON.stringify(props.data, null, 2)
  } catch {
    return String(props.data)
  }
})

// Catch rendering errors thrown by vue-json-pretty itself
onErrorCaptured(() => {
  renderError.value = true
  return false // prevent propagation
})
</script>

<template>
  <div>
    <!-- Nothing to show -->
    <span v-if="parsed === null && !rawFallback" class="text-gray-400 text-xs">—</span>

    <!-- vue-json-pretty errored during render — show raw -->
    <pre v-else-if="renderError" class="text-xs bg-gray-50 rounded p-2 overflow-x-auto whitespace-pre-wrap">{{ rawFallback }}</pre>

    <!-- Parsed successfully — use vue-json-pretty -->
    <VueJsonPretty
      v-else-if="parsed !== null"
      :data="parsed"
      :deep="2"
      :show-line="false"
    />

    <!-- String that failed JSON.parse — show raw -->
    <pre v-else class="text-xs bg-gray-50 rounded p-2 overflow-x-auto whitespace-pre-wrap">{{ rawFallback }}</pre>
  </div>
</template>
